import { AxiosHeaders } from 'axios'
import type { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { useTokenStore } from '@/features/auth/store'

interface StoredTokens {
  accessToken?: string
  refreshToken?: string
  expiresIn?: number
}

const REFRESH_ENDPOINT = 'auth/refresh_tokens'
const isClient = typeof window !== 'undefined'

const getStoredTokens = (): StoredTokens => {
  if (!isClient) {
    return {}
  }

  try {
    const tokens = window.localStorage.getItem('userTokens')
    return tokens ? JSON.parse(tokens) : {}
  } catch {
    return {}
  }
}

const ensureHeader = (config: InternalAxiosRequestConfig, key: string, value: string) => {
  if (!config.headers) {
    config.headers = new AxiosHeaders()
  }
  config.headers[key] = value
}

export const attachAuthInterceptors = (client: AxiosInstance): void => {
  client.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const tokenStore = useTokenStore()

      if (isClient) {
        const currency = window.localStorage.getItem('currency') ?? 'USD'
        ensureHeader(config, 'Currency', currency)

        const locale = window.localStorage.getItem('userLocale') ?? 'en'
        ensureHeader(config, 'lang', locale)
      } else {
        ensureHeader(config, 'Currency', 'USD')
        ensureHeader(config, 'lang', 'en')
      }

      if (tokenStore.accessToken && config.url !== REFRESH_ENDPOINT) {
        const storedTokens = getStoredTokens()
        if (Date.now() > (storedTokens.expiresIn ?? 0)) {
          // Single-flight inside the store: concurrent expired requests all
          // await one refresh instead of racing or skipping it.
          await tokenStore.updateTokens()
        }
      }

      // Attached after the refresh so a renewed token is the one sent
      if (tokenStore.accessToken) {
        ensureHeader(config, 'Authorization', `Bearer ${tokenStore.accessToken}`)
      }

      return config
    },
    (error: AxiosError) => Promise.reject(error)
  )

  client.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError & { config: InternalAxiosRequestConfig & { _retry?: boolean } }) => {
      const tokenStore = useTokenStore()
      const originalRequest = error.config

      const responseStatus = error.response?.status
      const responseData = (error.response?.data ?? {}) as unknown
      const isAccountMerge =
        responseStatus === 401 &&
        typeof responseData === 'object' &&
        responseData !== null &&
        'includes' in responseData &&
        typeof (responseData as { includes: (value: string) => boolean }).includes === 'function' &&
        (responseData as { includes: (value: string) => boolean }).includes('accounts have been successfully merged')

      if (isAccountMerge) {
        return Promise.reject(error)
      }

      if (
        responseStatus === 401 &&
        !originalRequest._retry &&
        originalRequest.url !== REFRESH_ENDPOINT
      ) {
        originalRequest._retry = true
        // The server already rejected this token — refresh even if the local
        // clock still considers it valid
        await tokenStore.updateTokens({ force: true })

        // Only retry if tokens were actually refreshed
        if (tokenStore.accessToken) {
          return client(originalRequest)
        }
      }

      return Promise.reject(error)
    }
  )
}

